/**
 * Job Title Aliases and Classifications
 * 
 * Maps generic O*NET/BLS job titles to actionable, searchable job titles
 * that users can actually find on job boards like Indeed, LinkedIn, etc.
 */

export interface JobTitleMapping {
  onet_code: string;
  generic_title: string;
  actionable_titles: string[];
  specializations: Specialization[];
  search_keywords: string[];
  industry_contexts: string[];
  premium_specializations?: PremiumSpecialization[];
}

export interface Specialization {
  title: string;
  description: string;
  typical_employers: string[];
  salary_modifier: number;
}

export interface PremiumSpecialization {
  title: string;
  description: string;
  requirements: string[];
  certifications: string[];
  salary_range: { min: number; max: number };
  employers: string[];
}

export const JOB_TITLE_MAPPINGS: JobTitleMapping[] = [
  // ============================================
  // HEALTHCARE CAREERS
  // ============================================
  {
    onet_code: "29-1299.00",
    generic_title: "Healthcare Diagnosing or Treating Practitioners, All Other",
    actionable_titles: ["Health Coach", "Wellness Coordinator", "Patient Navigator", "Community Health Worker", "Health Educator", "Care Coordinator"],
    specializations: [
      { title: "Health Coach", description: "Guide patients in lifestyle changes and wellness goals", typical_employers: ["Hospitals", "Insurance Companies", "Wellness Centers"], salary_modifier: 1.0 },
      { title: "Patient Navigator", description: "Help patients navigate healthcare system and treatment plans", typical_employers: ["Hospitals", "Cancer Centers", "Community Health Centers"], salary_modifier: 1.05 }
    ],
    search_keywords: ["health coach", "wellness coordinator", "patient navigator", "care coordinator"],
    industry_contexts: ["Healthcare", "Insurance", "Corporate Wellness"],
    premium_specializations: [{ title: "Oncology Patient Navigator", description: "Specialized navigator for cancer patients", requirements: ["Healthcare background", "Oncology knowledge"], certifications: ["OPN-CG"], salary_range: { min: 55000, max: 75000 }, employers: ["MD Anderson", "Memorial Sloan Kettering", "Mayo Clinic"] }]
  },
  {
    onet_code: "29-2061.00",
    generic_title: "Licensed Practical and Licensed Vocational Nurses",
    actionable_titles: ["LPN", "LVN", "Licensed Practical Nurse", "Licensed Vocational Nurse", "Clinic Nurse", "Staff Nurse"],
    specializations: [
      { title: "Home Health LPN", description: "Provide nursing care in patients' homes", typical_employers: ["Home Health Agencies", "Hospice"], salary_modifier: 1.05 },
      { title: "Long-Term Care LPN", description: "Nursing care in skilled nursing facilities", typical_employers: ["Nursing Homes", "Assisted Living"], salary_modifier: 1.0 }
    ],
    search_keywords: ["LPN", "LVN", "licensed practical nurse", "licensed vocational nurse"],
    industry_contexts: ["Healthcare", "Long-Term Care", "Home Health"]
  },
  {
    onet_code: "29-1141.00",
    generic_title: "Registered Nurses",
    actionable_titles: ["Registered Nurse", "RN", "Staff Nurse", "Charge Nurse", "Clinical Nurse", "Floor Nurse"],
    specializations: [
      { title: "ICU Nurse", description: "Critical care nursing in intensive care units", typical_employers: ["Hospitals"], salary_modifier: 1.2 },
      { title: "ER Nurse", description: "Emergency room nursing care", typical_employers: ["Hospitals", "Urgent Care"], salary_modifier: 1.15 },
      { title: "OR Nurse", description: "Operating room and surgical nursing", typical_employers: ["Hospitals", "Surgery Centers"], salary_modifier: 1.15 }
    ],
    search_keywords: ["registered nurse", "RN", "staff nurse", "clinical nurse", "hospital nurse"],
    industry_contexts: ["Healthcare", "Hospitals", "Clinics"]
  },
  {
    onet_code: "31-1131.00",
    generic_title: "Nursing Assistants",
    actionable_titles: ["CNA", "Certified Nursing Assistant", "Nurse Aide", "Patient Care Technician", "PCT", "Nursing Aide"],
    specializations: [
      { title: "Hospital CNA", description: "Nursing assistant in acute care settings", typical_employers: ["Hospitals"], salary_modifier: 1.1 },
      { title: "Long-Term Care CNA", description: "Care in nursing homes and assisted living", typical_employers: ["Nursing Homes", "Assisted Living"], salary_modifier: 1.0 }
    ],
    search_keywords: ["CNA", "certified nursing assistant", "nurse aide", "patient care technician", "PCT"],
    industry_contexts: ["Healthcare", "Long-Term Care"]
  },
  {
    onet_code: "29-2042.00",
    generic_title: "Emergency Medical Technicians",
    actionable_titles: ["EMT", "EMT-Basic", "Emergency Medical Technician", "Ambulance Technician", "First Responder"],
    specializations: [
      { title: "EMT-Basic", description: "Basic emergency medical care and transport", typical_employers: ["Ambulance Services", "Fire Departments"], salary_modifier: 1.0 },
      { title: "Paramedic", description: "Advanced emergency medical care", typical_employers: ["Ambulance Services", "Fire Departments", "Hospitals"], salary_modifier: 1.3 }
    ],
    search_keywords: ["EMT", "emergency medical technician", "paramedic", "ambulance", "first responder"],
    industry_contexts: ["Emergency Services", "Healthcare"]
  },
  {
    onet_code: "31-9092.00",
    generic_title: "Medical Assistants",
    actionable_titles: ["Medical Assistant", "Clinical Medical Assistant", "Certified Medical Assistant", "CMA", "Back Office Medical Assistant"],
    specializations: [
      { title: "Clinical Medical Assistant", description: "Patient care and clinical procedures", typical_employers: ["Medical Offices", "Clinics"], salary_modifier: 1.0 },
      { title: "Administrative Medical Assistant", description: "Front office and scheduling", typical_employers: ["Medical Offices", "Hospitals"], salary_modifier: 0.95 }
    ],
    search_keywords: ["medical assistant", "CMA", "clinical assistant", "back office medical"],
    industry_contexts: ["Healthcare", "Medical Offices"]
  },
  {
    onet_code: "31-9097.00",
    generic_title: "Phlebotomists",
    actionable_titles: ["Phlebotomist", "Phlebotomy Technician", "Blood Draw Technician", "Lab Phlebotomist"],
    specializations: [
      { title: "Hospital Phlebotomist", description: "Blood draws in hospital settings", typical_employers: ["Hospitals"], salary_modifier: 1.05 },
      { title: "Mobile Phlebotomist", description: "Travel to patients for blood draws", typical_employers: ["Mobile Labs", "Home Health"], salary_modifier: 1.1 }
    ],
    search_keywords: ["phlebotomist", "phlebotomy technician", "blood draw"],
    industry_contexts: ["Healthcare", "Laboratories"]
  },

  // ============================================
  // TECHNOLOGY CAREERS
  // ============================================
  {
    onet_code: "15-1232.00",
    generic_title: "Computer User Support Specialists",
    actionable_titles: ["IT Help Desk Technician", "Technical Support Specialist", "Desktop Support Technician", "IT Support Analyst", "Service Desk Analyst", "Tier 1 Support"],
    specializations: [
      { title: "Help Desk Technician", description: "First-line IT support and troubleshooting", typical_employers: ["All industries", "MSPs", "Tech companies"], salary_modifier: 1.0 },
      { title: "Desktop Support Technician", description: "On-site hardware/software support", typical_employers: ["Corporations", "Government", "Education"], salary_modifier: 1.05 }
    ],
    search_keywords: ["help desk", "IT support", "technical support", "desktop support", "tier 1 support"],
    industry_contexts: ["Technology", "All Industries"]
  },
  {
    onet_code: "15-1231.00",
    generic_title: "Computer Network Support Specialists",
    actionable_titles: ["Network Technician", "Network Support Specialist", "Network Administrator", "NOC Technician", "Network Engineer"],
    specializations: [
      { title: "NOC Technician", description: "Monitor and maintain network operations", typical_employers: ["Data Centers", "MSPs", "Telecom"], salary_modifier: 1.0 },
      { title: "Network Administrator", description: "Manage and configure network infrastructure", typical_employers: ["Corporations", "Government"], salary_modifier: 1.15 }
    ],
    search_keywords: ["network technician", "network support", "network administrator", "NOC"],
    industry_contexts: ["Technology", "Telecommunications"]
  },
  {
    onet_code: "15-1212.00",
    generic_title: "Information Security Analysts",
    actionable_titles: ["Security Analyst", "Cybersecurity Analyst", "SOC Analyst", "Information Security Analyst", "Security Engineer"],
    specializations: [
      { title: "SOC Analyst", description: "Monitor security operations center", typical_employers: ["MSSPs", "Large Enterprises", "Government"], salary_modifier: 1.0 },
      { title: "Penetration Tester", description: "Test systems for vulnerabilities", typical_employers: ["Security Firms", "Consulting"], salary_modifier: 1.2 }
    ],
    search_keywords: ["security analyst", "cybersecurity analyst", "SOC analyst", "infosec"],
    industry_contexts: ["Technology", "Finance", "Government"]
  },
  {
    onet_code: "15-1256.00",
    generic_title: "Software Developers and Software Quality Assurance Analysts and Testers",
    actionable_titles: ["Software Developer", "Software Engineer", "Programmer", "Full Stack Developer", "Backend Developer", "Frontend Developer", "QA Engineer"],
    specializations: [
      { title: "Frontend Developer", description: "Build user interfaces and web applications", typical_employers: ["Tech Companies", "Agencies"], salary_modifier: 1.0 },
      { title: "Backend Developer", description: "Build server-side applications and APIs", typical_employers: ["Tech Companies", "Startups"], salary_modifier: 1.05 },
      { title: "Full Stack Developer", description: "Work on both frontend and backend", typical_employers: ["Startups", "Tech Companies"], salary_modifier: 1.1 }
    ],
    search_keywords: ["software developer", "software engineer", "programmer", "web developer", "full stack"],
    industry_contexts: ["Technology", "All Industries"]
  },
  {
    onet_code: "15-1299.00",
    generic_title: "Computer Occupations, All Other",
    actionable_titles: ["IT Specialist", "Systems Administrator", "Cloud Engineer", "DevOps Engineer", "Site Reliability Engineer"],
    specializations: [
      { title: "Cloud Engineer", description: "Design and manage cloud infrastructure", typical_employers: ["Tech Companies", "Enterprises"], salary_modifier: 1.2 },
      { title: "DevOps Engineer", description: "Automate development and operations", typical_employers: ["Tech Companies", "Startups"], salary_modifier: 1.25 }
    ],
    search_keywords: ["IT specialist", "systems administrator", "cloud engineer", "DevOps"],
    industry_contexts: ["Technology"]
  },
  {
    onet_code: "15-1211.00",
    generic_title: "Computer Systems Analysts",
    actionable_titles: ["Systems Analyst", "Business Systems Analyst", "IT Analyst", "Technical Analyst", "Application Analyst"],
    specializations: [
      { title: "Business Systems Analyst", description: "Bridge business needs and IT solutions", typical_employers: ["Corporations", "Consulting"], salary_modifier: 1.1 },
      { title: "ERP Analyst", description: "Specialize in enterprise resource planning systems", typical_employers: ["Large Enterprises", "Consulting"], salary_modifier: 1.15 }
    ],
    search_keywords: ["systems analyst", "business analyst", "IT analyst", "technical analyst"],
    industry_contexts: ["Technology", "Consulting", "Finance"]
  },

  // ============================================
  // MANAGEMENT CAREERS
  // ============================================
  {
    onet_code: "11-9199.00",
    generic_title: "Managers, All Other",
    actionable_titles: ["Operations Manager", "Program Manager", "Department Manager", "Branch Manager", "Area Manager", "Office Manager", "General Manager"],
    specializations: [
      { title: "Operations Manager", description: "Oversee daily operations and efficiency", typical_employers: ["Manufacturing", "Logistics", "Retail", "Healthcare"], salary_modifier: 1.15 },
      { title: "Program Manager", description: "Manage multiple related projects", typical_employers: ["Tech Companies", "Non-profits", "Government"], salary_modifier: 1.2 }
    ],
    search_keywords: ["operations manager", "program manager", "department manager", "branch manager", "general manager"],
    industry_contexts: ["All Industries"]
  },
  {
    onet_code: "11-1021.00",
    generic_title: "General and Operations Managers",
    actionable_titles: ["General Manager", "Operations Manager", "Plant Manager", "Facility Manager", "Site Manager", "Store Manager"],
    specializations: [
      { title: "Plant Manager", description: "Manage manufacturing facility operations", typical_employers: ["Manufacturing"], salary_modifier: 1.2 },
      { title: "Store Manager", description: "Manage retail store operations", typical_employers: ["Retail Chains"], salary_modifier: 1.0 }
    ],
    search_keywords: ["general manager", "operations manager", "plant manager", "facility manager"],
    industry_contexts: ["Manufacturing", "Retail", "Logistics"]
  },
  {
    onet_code: "11-3031.00",
    generic_title: "Financial Managers",
    actionable_titles: ["Finance Manager", "Controller", "Accounting Manager", "Financial Controller", "Director of Finance"],
    specializations: [
      { title: "Controller", description: "Oversee accounting and financial reporting", typical_employers: ["Corporations", "Non-profits"], salary_modifier: 1.15 },
      { title: "FP&A Manager", description: "Financial planning and analysis", typical_employers: ["Corporations"], salary_modifier: 1.2 }
    ],
    search_keywords: ["finance manager", "controller", "accounting manager", "financial controller"],
    industry_contexts: ["All Industries", "Finance"]
  },
  {
    onet_code: "11-3121.00",
    generic_title: "Human Resources Managers",
    actionable_titles: ["HR Manager", "Human Resources Manager", "People Manager", "HR Director", "Talent Manager"],
    specializations: [
      { title: "Talent Acquisition Manager", description: "Lead recruiting and hiring", typical_employers: ["Corporations", "Staffing"], salary_modifier: 1.1 },
      { title: "HR Business Partner", description: "Strategic HR support for business units", typical_employers: ["Large Corporations"], salary_modifier: 1.15 }
    ],
    search_keywords: ["HR manager", "human resources manager", "people manager", "HR director"],
    industry_contexts: ["All Industries"]
  },
  {
    onet_code: "11-2021.00",
    generic_title: "Marketing Managers",
    actionable_titles: ["Marketing Manager", "Brand Manager", "Digital Marketing Manager", "Marketing Director", "Growth Manager"],
    specializations: [
      { title: "Digital Marketing Manager", description: "Lead digital marketing strategy", typical_employers: ["Tech Companies", "Agencies", "E-commerce"], salary_modifier: 1.1 },
      { title: "Product Marketing Manager", description: "Marketing for product launches", typical_employers: ["Tech Companies", "Consumer Goods"], salary_modifier: 1.15 }
    ],
    search_keywords: ["marketing manager", "brand manager", "digital marketing manager", "growth manager"],
    industry_contexts: ["All Industries", "Technology", "Consumer Goods"]
  },
  {
    onet_code: "11-2022.00",
    generic_title: "Sales Managers",
    actionable_titles: ["Sales Manager", "Regional Sales Manager", "Sales Director", "Account Manager", "Business Development Manager"],
    specializations: [
      { title: "Regional Sales Manager", description: "Manage sales team for a region", typical_employers: ["All Industries"], salary_modifier: 1.1 },
      { title: "Enterprise Sales Manager", description: "Lead enterprise sales team", typical_employers: ["Tech Companies", "B2B"], salary_modifier: 1.25 }
    ],
    search_keywords: ["sales manager", "regional sales manager", "sales director", "business development manager"],
    industry_contexts: ["All Industries"]
  },
  {
    onet_code: "11-3021.00",
    generic_title: "Computer and Information Systems Managers",
    actionable_titles: ["IT Manager", "IT Director", "Technology Manager", "Infrastructure Manager", "Engineering Manager"],
    specializations: [
      { title: "IT Director", description: "Lead IT department and strategy", typical_employers: ["Corporations", "Government"], salary_modifier: 1.2 },
      { title: "Engineering Manager", description: "Manage software engineering teams", typical_employers: ["Tech Companies"], salary_modifier: 1.25 }
    ],
    search_keywords: ["IT manager", "IT director", "technology manager", "engineering manager"],
    industry_contexts: ["Technology", "All Industries"]
  },

  // ============================================
  // ADMINISTRATIVE CAREERS
  // ============================================
  {
    onet_code: "43-6014.00",
    generic_title: "Secretaries and Administrative Assistants, Except Legal, Medical, and Executive",
    actionable_titles: ["Administrative Assistant", "Office Administrator", "Administrative Coordinator", "Office Coordinator", "Admin Assistant"],
    specializations: [
      { title: "Executive Assistant", description: "High-level support for executives", typical_employers: ["Corporations", "Non-profits"], salary_modifier: 1.3 },
      { title: "Project Coordinator", description: "Administrative support for projects", typical_employers: ["Construction", "IT", "Marketing"], salary_modifier: 1.15 }
    ],
    search_keywords: ["administrative assistant", "office administrator", "admin assistant", "office coordinator"],
    industry_contexts: ["All Industries"]
  },
  {
    onet_code: "43-6011.00",
    generic_title: "Executive Secretaries and Executive Administrative Assistants",
    actionable_titles: ["Executive Assistant", "Executive Secretary", "Senior Administrative Assistant", "C-Suite Assistant", "EA"],
    specializations: [
      { title: "C-Suite Executive Assistant", description: "Support CEO, CFO, or other executives", typical_employers: ["Fortune 500", "Private Equity"], salary_modifier: 1.3 }
    ],
    search_keywords: ["executive assistant", "executive secretary", "EA", "senior admin"],
    industry_contexts: ["All Industries"]
  },
  {
    onet_code: "43-4171.00",
    generic_title: "Receptionists and Information Clerks",
    actionable_titles: ["Receptionist", "Front Desk Receptionist", "Front Desk Coordinator", "Lobby Attendant", "Information Clerk"],
    specializations: [
      { title: "Medical Receptionist", description: "Front desk in healthcare settings", typical_employers: ["Medical Offices", "Hospitals"], salary_modifier: 1.05 },
      { title: "Corporate Receptionist", description: "Front desk in corporate offices", typical_employers: ["Corporations"], salary_modifier: 1.0 }
    ],
    search_keywords: ["receptionist", "front desk", "front desk coordinator"],
    industry_contexts: ["All Industries"]
  },
  {
    onet_code: "43-3011.00",
    generic_title: "Bill and Account Collectors",
    actionable_titles: ["Collections Specialist", "Account Collector", "Debt Collector", "Collections Representative", "AR Specialist"],
    specializations: [
      { title: "Medical Collections Specialist", description: "Collect on medical accounts", typical_employers: ["Healthcare", "Collection Agencies"], salary_modifier: 1.05 },
      { title: "Commercial Collections", description: "B2B debt collection", typical_employers: ["Collection Agencies", "Finance"], salary_modifier: 1.1 }
    ],
    search_keywords: ["collections specialist", "debt collector", "account collector", "AR specialist"],
    industry_contexts: ["Finance", "Healthcare", "Collections"]
  },
  {
    onet_code: "43-3031.00",
    generic_title: "Bookkeeping, Accounting, and Auditing Clerks",
    actionable_titles: ["Bookkeeper", "Accounting Clerk", "Accounts Payable Clerk", "Accounts Receivable Clerk", "AP/AR Clerk", "Staff Accountant"],
    specializations: [
      { title: "Full Charge Bookkeeper", description: "Handle all bookkeeping for small business", typical_employers: ["Small Businesses", "Accounting Firms"], salary_modifier: 1.15 },
      { title: "Accounts Payable Specialist", description: "Process vendor payments", typical_employers: ["All Industries"], salary_modifier: 1.0 }
    ],
    search_keywords: ["bookkeeper", "accounting clerk", "accounts payable", "accounts receivable", "AP clerk"],
    industry_contexts: ["All Industries"]
  },

  // ============================================
  // CUSTOMER SERVICE & SALES
  // ============================================
  {
    onet_code: "43-4051.00",
    generic_title: "Customer Service Representatives",
    actionable_titles: ["Customer Service Representative", "Customer Support Specialist", "Client Services Representative", "Member Services Representative", "Call Center Representative", "Customer Care Agent"],
    specializations: [
      { title: "Technical Support Representative", description: "Customer service with technical focus", typical_employers: ["Tech Companies", "Software Vendors"], salary_modifier: 1.15 },
      { title: "Financial Services Representative", description: "Customer service for banking/insurance", typical_employers: ["Banks", "Credit Unions", "Insurance"], salary_modifier: 1.1 }
    ],
    search_keywords: ["customer service", "customer support", "client services", "call center", "customer care"],
    industry_contexts: ["All Industries"]
  },
  {
    onet_code: "41-3091.00",
    generic_title: "Sales Representatives of Services, Except Advertising, Insurance, Financial Services, and Travel",
    actionable_titles: ["Sales Representative", "Account Executive", "Business Development Representative", "Inside Sales Representative", "Territory Sales Representative", "Sales Consultant"],
    specializations: [
      { title: "SaaS Sales Representative", description: "Sell software-as-a-service products", typical_employers: ["Tech Startups", "Software Companies"], salary_modifier: 1.3 },
      { title: "Medical Device Sales", description: "Sell medical equipment to healthcare", typical_employers: ["Medical Device Manufacturers"], salary_modifier: 1.4 }
    ],
    search_keywords: ["sales representative", "account executive", "BDR", "SDR", "inside sales"],
    industry_contexts: ["Technology", "Healthcare", "Professional Services"]
  },
  {
    onet_code: "41-4012.00",
    generic_title: "Sales Representatives, Wholesale and Manufacturing, Except Technical and Scientific Products",
    actionable_titles: ["Outside Sales Representative", "Territory Manager", "Account Manager", "Sales Executive", "Field Sales Representative"],
    specializations: [
      { title: "Territory Manager", description: "Manage sales in geographic territory", typical_employers: ["Manufacturing", "Distribution"], salary_modifier: 1.1 },
      { title: "Key Account Manager", description: "Manage major customer accounts", typical_employers: ["All Industries"], salary_modifier: 1.2 }
    ],
    search_keywords: ["outside sales", "territory manager", "account manager", "field sales"],
    industry_contexts: ["Manufacturing", "Distribution", "Wholesale"]
  },
  {
    onet_code: "41-2031.00",
    generic_title: "Retail Salespersons",
    actionable_titles: ["Sales Associate", "Retail Sales Associate", "Sales Consultant", "Store Associate", "Sales Specialist"],
    specializations: [
      { title: "Luxury Sales Associate", description: "High-end retail sales", typical_employers: ["Luxury Retailers", "Department Stores"], salary_modifier: 1.2 },
      { title: "Electronics Sales Specialist", description: "Sell electronics and technology", typical_employers: ["Electronics Retailers"], salary_modifier: 1.1 }
    ],
    search_keywords: ["sales associate", "retail sales", "store associate", "sales consultant"],
    industry_contexts: ["Retail"]
  },

  // ============================================
  // TRADES & SKILLED LABOR
  // ============================================
  {
    onet_code: "47-2111.00",
    generic_title: "Electricians",
    actionable_titles: ["Electrician", "Journeyman Electrician", "Apprentice Electrician", "Industrial Electrician", "Commercial Electrician", "Residential Electrician"],
    specializations: [
      { title: "Industrial Electrician", description: "Electrical work in manufacturing", typical_employers: ["Manufacturing Plants", "Factories"], salary_modifier: 1.15 },
      { title: "Commercial Electrician", description: "Electrical work in commercial buildings", typical_employers: ["Contractors", "Commercial Construction"], salary_modifier: 1.1 }
    ],
    search_keywords: ["electrician", "journeyman electrician", "industrial electrician", "commercial electrician"],
    industry_contexts: ["Construction", "Manufacturing"]
  },
  {
    onet_code: "47-2152.00",
    generic_title: "Plumbers, Pipefitters, and Steamfitters",
    actionable_titles: ["Plumber", "Journeyman Plumber", "Pipefitter", "Service Plumber", "Commercial Plumber"],
    specializations: [
      { title: "Service Plumber", description: "Residential plumbing repairs", typical_employers: ["Plumbing Companies"], salary_modifier: 1.0 },
      { title: "Commercial Plumber", description: "Plumbing in commercial buildings", typical_employers: ["Commercial Contractors"], salary_modifier: 1.1 }
    ],
    search_keywords: ["plumber", "journeyman plumber", "pipefitter", "service plumber"],
    industry_contexts: ["Construction", "Facilities"]
  },
  {
    onet_code: "49-9021.00",
    generic_title: "Heating, Air Conditioning, and Refrigeration Mechanics and Installers",
    actionable_titles: ["HVAC Technician", "HVAC Installer", "HVAC Mechanic", "Refrigeration Technician", "AC Technician"],
    specializations: [
      { title: "Commercial HVAC Technician", description: "HVAC for commercial buildings", typical_employers: ["HVAC Companies", "Facilities"], salary_modifier: 1.1 },
      { title: "Refrigeration Technician", description: "Commercial refrigeration systems", typical_employers: ["Restaurants", "Grocery", "Industrial"], salary_modifier: 1.15 }
    ],
    search_keywords: ["HVAC technician", "HVAC installer", "AC technician", "refrigeration technician"],
    industry_contexts: ["Construction", "Facilities", "Manufacturing"]
  },
  {
    onet_code: "51-4121.00",
    generic_title: "Welders, Cutters, Solderers, and Brazers",
    actionable_titles: ["Welder", "MIG Welder", "TIG Welder", "Structural Welder", "Pipe Welder", "Fabricator"],
    specializations: [
      { title: "Pipe Welder", description: "Welding pipes and pipelines", typical_employers: ["Oil & Gas", "Construction"], salary_modifier: 1.2 },
      { title: "Structural Welder", description: "Welding structural steel", typical_employers: ["Construction", "Manufacturing"], salary_modifier: 1.1 }
    ],
    search_keywords: ["welder", "MIG welder", "TIG welder", "pipe welder", "fabricator"],
    industry_contexts: ["Manufacturing", "Construction", "Oil & Gas"]
  },
  {
    onet_code: "49-3023.00",
    generic_title: "Automotive Service Technicians and Mechanics",
    actionable_titles: ["Auto Mechanic", "Automotive Technician", "Service Technician", "Master Technician", "Diesel Mechanic"],
    specializations: [
      { title: "Master Technician", description: "Advanced automotive diagnostics and repair", typical_employers: ["Dealerships", "Auto Shops"], salary_modifier: 1.2 },
      { title: "Diesel Mechanic", description: "Repair diesel engines and trucks", typical_employers: ["Trucking", "Fleet Services"], salary_modifier: 1.15 }
    ],
    search_keywords: ["auto mechanic", "automotive technician", "service technician", "diesel mechanic"],
    industry_contexts: ["Automotive", "Transportation"]
  },
  {
    onet_code: "53-3032.00",
    generic_title: "Heavy and Tractor-Trailer Truck Drivers",
    actionable_titles: ["Truck Driver", "CDL Driver", "OTR Driver", "Local Driver", "Delivery Driver", "Semi Truck Driver"],
    specializations: [
      { title: "OTR Driver", description: "Over-the-road long-haul trucking", typical_employers: ["Trucking Companies", "Freight"], salary_modifier: 1.1 },
      { title: "Local/Regional Driver", description: "Short-haul and regional routes", typical_employers: ["Distribution", "Delivery"], salary_modifier: 1.0 }
    ],
    search_keywords: ["truck driver", "CDL driver", "OTR driver", "semi driver", "delivery driver"],
    industry_contexts: ["Transportation", "Logistics"]
  },

  // ============================================
  // LOGISTICS & WAREHOUSE
  // ============================================
  {
    onet_code: "53-7062.00",
    generic_title: "Laborers and Freight, Stock, and Material Movers, Hand",
    actionable_titles: ["Warehouse Associate", "Material Handler", "Picker/Packer", "Warehouse Worker", "Shipping and Receiving Clerk"],
    specializations: [
      { title: "Forklift Operator", description: "Operate forklifts in warehouse", typical_employers: ["Warehouses", "Distribution Centers"], salary_modifier: 1.1 },
      { title: "Order Picker", description: "Pick orders for fulfillment", typical_employers: ["E-commerce", "Distribution"], salary_modifier: 1.0 }
    ],
    search_keywords: ["warehouse associate", "material handler", "picker packer", "warehouse worker"],
    industry_contexts: ["Logistics", "Warehousing", "E-commerce"]
  },
  {
    onet_code: "43-5071.00",
    generic_title: "Shipping, Receiving, and Inventory Clerks",
    actionable_titles: ["Shipping Clerk", "Receiving Clerk", "Inventory Clerk", "Shipping and Receiving Coordinator", "Logistics Clerk"],
    specializations: [
      { title: "Inventory Control Specialist", description: "Manage inventory accuracy", typical_employers: ["Warehouses", "Retail"], salary_modifier: 1.1 },
      { title: "Shipping Coordinator", description: "Coordinate outbound shipments", typical_employers: ["Distribution", "Manufacturing"], salary_modifier: 1.05 }
    ],
    search_keywords: ["shipping clerk", "receiving clerk", "inventory clerk", "logistics clerk"],
    industry_contexts: ["Logistics", "Warehousing", "Manufacturing"]
  },
  {
    onet_code: "13-1081.00",
    generic_title: "Logisticians",
    actionable_titles: ["Logistics Coordinator", "Supply Chain Analyst", "Logistics Analyst", "Supply Chain Coordinator", "Logistics Specialist"],
    specializations: [
      { title: "Supply Chain Analyst", description: "Analyze and optimize supply chain", typical_employers: ["Manufacturing", "Retail", "Consulting"], salary_modifier: 1.15 },
      { title: "Transportation Coordinator", description: "Coordinate freight and shipping", typical_employers: ["Logistics", "3PLs"], salary_modifier: 1.05 }
    ],
    search_keywords: ["logistics coordinator", "supply chain analyst", "logistics analyst", "supply chain coordinator"],
    industry_contexts: ["Logistics", "Manufacturing", "Retail"]
  },

  // ============================================
  // FINANCE & ACCOUNTING
  // ============================================
  {
    onet_code: "13-2011.00",
    generic_title: "Accountants and Auditors",
    actionable_titles: ["Staff Accountant", "Accountant", "Senior Accountant", "Tax Accountant", "Auditor", "CPA"],
    specializations: [
      { title: "Tax Accountant", description: "Prepare tax returns and tax planning", typical_employers: ["CPA Firms", "Corporations"], salary_modifier: 1.1 },
      { title: "Auditor", description: "Conduct financial audits", typical_employers: ["CPA Firms", "Government"], salary_modifier: 1.15 }
    ],
    search_keywords: ["accountant", "staff accountant", "tax accountant", "auditor", "CPA"],
    industry_contexts: ["Accounting", "Finance", "All Industries"]
  },
  {
    onet_code: "13-2051.00",
    generic_title: "Financial Analysts",
    actionable_titles: ["Financial Analyst", "FP&A Analyst", "Investment Analyst", "Budget Analyst", "Business Analyst"],
    specializations: [
      { title: "FP&A Analyst", description: "Financial planning and analysis", typical_employers: ["Corporations"], salary_modifier: 1.1 },
      { title: "Investment Analyst", description: "Analyze investment opportunities", typical_employers: ["Investment Firms", "Banks"], salary_modifier: 1.2 }
    ],
    search_keywords: ["financial analyst", "FP&A analyst", "investment analyst", "budget analyst"],
    industry_contexts: ["Finance", "Banking", "Corporations"]
  },
  {
    onet_code: "43-3071.00",
    generic_title: "Tellers",
    actionable_titles: ["Bank Teller", "Teller", "Customer Service Representative", "Personal Banker", "Branch Associate"],
    specializations: [
      { title: "Personal Banker", description: "Help customers with banking products", typical_employers: ["Banks", "Credit Unions"], salary_modifier: 1.15 },
      { title: "Head Teller", description: "Supervise teller operations", typical_employers: ["Banks", "Credit Unions"], salary_modifier: 1.1 }
    ],
    search_keywords: ["bank teller", "teller", "personal banker", "branch associate"],
    industry_contexts: ["Banking", "Financial Services"]
  },
  {
    onet_code: "13-2072.00",
    generic_title: "Loan Officers",
    actionable_titles: ["Loan Officer", "Mortgage Loan Officer", "Commercial Loan Officer", "Loan Originator", "Lending Specialist"],
    specializations: [
      { title: "Mortgage Loan Officer", description: "Originate home loans", typical_employers: ["Banks", "Mortgage Companies"], salary_modifier: 1.2 },
      { title: "Commercial Loan Officer", description: "Business and commercial lending", typical_employers: ["Banks"], salary_modifier: 1.25 }
    ],
    search_keywords: ["loan officer", "mortgage loan officer", "loan originator", "lending specialist"],
    industry_contexts: ["Banking", "Mortgage", "Financial Services"]
  },

  // ============================================
  // HUMAN RESOURCES
  // ============================================
  {
    onet_code: "13-1071.00",
    generic_title: "Human Resources Specialists",
    actionable_titles: ["HR Specialist", "HR Generalist", "HR Coordinator", "Recruiter", "Talent Acquisition Specialist", "HR Representative"],
    specializations: [
      { title: "Recruiter", description: "Source and hire candidates", typical_employers: ["All Industries", "Staffing Agencies"], salary_modifier: 1.1 },
      { title: "HR Generalist", description: "Handle various HR functions", typical_employers: ["All Industries"], salary_modifier: 1.05 }
    ],
    search_keywords: ["HR specialist", "HR generalist", "HR coordinator", "recruiter", "talent acquisition"],
    industry_contexts: ["All Industries"]
  },
  {
    onet_code: "43-4161.00",
    generic_title: "Human Resources Assistants, Except Payroll and Timekeeping",
    actionable_titles: ["HR Assistant", "HR Administrative Assistant", "HR Clerk", "Personnel Assistant", "Recruiting Coordinator"],
    specializations: [
      { title: "Recruiting Coordinator", description: "Coordinate interview scheduling", typical_employers: ["All Industries"], salary_modifier: 1.05 },
      { title: "Onboarding Coordinator", description: "Manage new hire onboarding", typical_employers: ["All Industries"], salary_modifier: 1.0 }
    ],
    search_keywords: ["HR assistant", "HR administrative assistant", "recruiting coordinator"],
    industry_contexts: ["All Industries"]
  },

  // ============================================
  // FOOD SERVICE & HOSPITALITY
  // ============================================
  {
    onet_code: "35-2014.00",
    generic_title: "Cooks, Restaurant",
    actionable_titles: ["Line Cook", "Prep Cook", "Cook", "Kitchen Staff", "Grill Cook", "Fry Cook"],
    specializations: [
      { title: "Line Cook", description: "Cook on the line during service", typical_employers: ["Restaurants"], salary_modifier: 1.0 },
      { title: "Sous Chef", description: "Second in command in kitchen", typical_employers: ["Restaurants", "Hotels"], salary_modifier: 1.3 }
    ],
    search_keywords: ["line cook", "prep cook", "cook", "kitchen staff", "grill cook"],
    industry_contexts: ["Food Service", "Hospitality"]
  },
  {
    onet_code: "35-3031.00",
    generic_title: "Waiters and Waitresses",
    actionable_titles: ["Server", "Waiter", "Waitress", "Food Server", "Wait Staff"],
    specializations: [
      { title: "Fine Dining Server", description: "Upscale restaurant service", typical_employers: ["Fine Dining Restaurants"], salary_modifier: 1.3 },
      { title: "Bartender", description: "Mix and serve drinks", typical_employers: ["Bars", "Restaurants"], salary_modifier: 1.2 }
    ],
    search_keywords: ["server", "waiter", "waitress", "food server"],
    industry_contexts: ["Food Service", "Hospitality"]
  },
  {
    onet_code: "35-1012.00",
    generic_title: "First-Line Supervisors of Food Preparation and Serving Workers",
    actionable_titles: ["Restaurant Manager", "Kitchen Manager", "Shift Manager", "Assistant Manager", "FOH Manager"],
    specializations: [
      { title: "Kitchen Manager", description: "Manage kitchen operations", typical_employers: ["Restaurants"], salary_modifier: 1.1 },
      { title: "Restaurant General Manager", description: "Manage entire restaurant", typical_employers: ["Restaurants", "Chains"], salary_modifier: 1.25 }
    ],
    search_keywords: ["restaurant manager", "kitchen manager", "shift manager", "assistant manager"],
    industry_contexts: ["Food Service", "Hospitality"]
  },
  {
    onet_code: "37-2011.00",
    generic_title: "Janitors and Cleaners, Except Maids and Housekeeping Cleaners",
    actionable_titles: ["Janitor", "Custodian", "Cleaner", "Building Maintenance", "Facilities Attendant"],
    specializations: [
      { title: "Commercial Cleaner", description: "Clean commercial buildings", typical_employers: ["Cleaning Companies", "Facilities"], salary_modifier: 1.0 },
      { title: "Industrial Cleaner", description: "Clean industrial facilities", typical_employers: ["Manufacturing", "Industrial"], salary_modifier: 1.1 }
    ],
    search_keywords: ["janitor", "custodian", "cleaner", "building maintenance"],
    industry_contexts: ["Facilities", "All Industries"]
  }
];

export interface EnhancedCareerResult {
  title: string;
  onet_code?: string;
  description: string;
  salary: { entry_min: number; entry_max: number };
  actionable_titles: string[];
  specializations: Specialization[];
  search_keywords: string[];
  industry_contexts: string[];
  has_premium_data: boolean;
  premium_preview: { title: string; salary_range: { min: number; max: number } }[];
}

export function getActionableTitles(onetCode: string): string[] {
  const mapping = JOB_TITLE_MAPPINGS.find(m => m.onet_code === onetCode);
  return mapping?.actionable_titles || [];
}

export function getSpecializations(onetCode: string): Specialization[] {
  const mapping = JOB_TITLE_MAPPINGS.find(m => m.onet_code === onetCode);
  return mapping?.specializations || [];
}

export function findMappingByTitle(title: string): JobTitleMapping | undefined {
  const lowerTitle = title.toLowerCase();
  return JOB_TITLE_MAPPINGS.find(m => 
    m.generic_title.toLowerCase().includes(lowerTitle) ||
    lowerTitle.includes(m.generic_title.toLowerCase()) ||
    m.actionable_titles.some(t => t.toLowerCase().includes(lowerTitle))
  );
}

export function enhanceCareerResult(career: {
  title: string;
  onet_code?: string;
  description: string;
  salary: { entry_min: number; entry_max: number };
}): EnhancedCareerResult {
  const mapping = career.onet_code 
    ? JOB_TITLE_MAPPINGS.find(m => m.onet_code === career.onet_code)
    : findMappingByTitle(career.title);

  if (!mapping) {
    return {
      ...career,
      actionable_titles: [career.title],
      specializations: [],
      search_keywords: [career.title.toLowerCase()],
      industry_contexts: [],
      has_premium_data: false,
      premium_preview: []
    };
  }

  return {
    ...career,
    actionable_titles: mapping.actionable_titles,
    specializations: mapping.specializations,
    search_keywords: mapping.search_keywords,
    industry_contexts: mapping.industry_contexts || [],
    has_premium_data: (mapping.premium_specializations?.length || 0) > 0,
    premium_preview: mapping.premium_specializations?.slice(0, 1).map(p => ({
      title: p.title,
      salary_range: p.salary_range
    })) || []
  };
}
